import React from 'react';
// Required for getTimeExtension
import dayjs from 'utils/dayjs-duration';
import DateUtils from 'utils/date';
import TimeUtils from 'utils/time';
import SpotDetailsReservationSummary from 'common/spot-details/reservation-summary';
import useIsMobile from 'hooks/use-is-mobile';
import { useSelector } from 'react-redux';
import { usePrice } from 'hooks/spot/price/use-price';
import { breakdownWODiscountGenerator } from 'utils/multiday-utils';
import InOutReservationSummary from 'pages/search-transient-event-monthly/components/spot-details-transient/inout-reservarion-summary';
import FormatUtils from 'utils/format';
import { Box, Button } from '@spothero/ui';
import { useLabel } from 'hooks/spot/price/use-label';
import { useShowTotal } from 'store/selectors/hooks/useShowTotal';
import { getSpotheroOversizeFee, OversizeType } from 'utils/search-vehicle';
export const ReservationSummary = ({ spot, isSticky, isStickyVersionShowing, isNonRefundable, isLoadingRates, showSpotUnavailable, onBookSpotClick, onToggleEditTimesModal, onShowRateDetailsClick, spotUnavailableDueToVehicleRestriction = false, }) => {
    const isMobile = useIsMobile();
    const { starts, ends, powerBooking: isPowerBooking, powerBookingPeriods, partner, } = useSelector((state) => state.searchRequest);
    const showTotal = useShowTotal();
    const price = usePrice(spot);
    const { selectedRate, facility: { reservationExtensionEnabled }, availability, tieredAvailability, } = spot;
    const priceLabelString = useLabel({
        selectedRate,
    });
    // Normalize the spot's selectedRate to camelCase priceBreakdown so getSpotheroOversizeFee
    // can find the oversize fee (spot details uses snake_case price_breakdown.items).
    const mappedPriceBreakdown = selectedRate?.price_breakdown?.items?.map(({ price: localPrice, type, priceBreakdown: pb }) => ({
        price: {
            value: localPrice,
            currencyCode: price.currencyCode?.toUpperCase() || '',
        },
        priceBreakdown: pb,
        type,
    }));
    const normalizedSpot = {
        ...spot,
        // v2→v1 transformation renames `vehicle` to `facilityVehicle`;
        // restore the expected field name so getSpotheroOversizeFee can find it.
        vehicle: spot.facilityVehicle,
        selectedRate: selectedRate
            ? { ...selectedRate, priceBreakdown: mappedPriceBreakdown }
            : selectedRate,
    };
    // Below two lines ensure oversized spots that are collected on site have a star.
    const { isOversize, type: oversizedType } = getSpotheroOversizeFee(normalizedSpot);
    const labelOversizeIncluded = isOversize && oversizedType === OversizeType.ChargedOnsiteKnown
        ? 'Total*'
        : priceLabelString;
    const appleMapsPartner = partner === 'apple' || partner === 'appleTest'; // Could move to its own hook
    let priceWithoutDiscount;
    let reservationDateTimeFormatted = '';
    let isReservationExtended = false;
    const rateAmenities = selectedRate?.amenities || [];
    const hasInOutAmenity = Boolean(rateAmenities.find(amenity => amenity?.slug === 'in-out'));
    if (selectedRate) {
        const { isExtended } = TimeUtils.getTimeExtension({
            actualStartDateTimeObj: dayjs(selectedRate.starts),
            actualEndDateTimeObj: dayjs(selectedRate.ends),
            requestedStartDateTimeObj: dayjs(starts),
            requestedEndDateTimeObj: dayjs(ends),
            isMonthly: false,
        });
        isReservationExtended = isExtended;
        // Ideally this map can be streamlined when we do full search v2 crossover
        try {
            let priceBreakdown;
            const currencyCode = price.currencyCode.toUpperCase();
            if (isPowerBooking) {
                // For Power Booking, use the already-aggregated powerBookingPriceBreakdown
                priceBreakdown = selectedRate?.powerBookingPriceBreakdown || [];
            }
            else {
                // For regular bookings, map the price_breakdown.items
                priceBreakdown = selectedRate?.price_breakdown.items.map(
                // shorthand used due to scoping
                ({ price: localPrice, type, priceBreakdown: pb }) => ({
                    price: {
                        value: localPrice,
                        currencyCode,
                    },
                    priceBreakdown: pb,
                    type,
                }));
            }
            priceWithoutDiscount = breakdownWODiscountGenerator(priceBreakdown, showTotal);
        }
        catch {
            // Fail silently
        }
        if (isPowerBooking) {
            const reservationStartTime = powerBookingPeriods?.length
                ? dayjs(powerBookingPeriods[0].starts).format('h:mm A')
                : '';
            const reservationEndTime = powerBookingPeriods?.length
                ? dayjs(powerBookingPeriods[0].ends).format('h:mm A')
                : '';
            reservationDateTimeFormatted = (<Box data-testid="ReservationSummary-power-booking-period">
                    {powerBookingPeriods.length}{' '}
                    {FormatUtils.pluralize(powerBookingPeriods.length, 'day', 'days')}{' '}
                    <Box as="span" color="gray.200" fontWeight="light">
                        |
                    </Box>{' '}
                    {reservationStartTime} - {reservationEndTime}
                </Box>);
            isReservationExtended = false;
        }
        else {
            reservationDateTimeFormatted = DateUtils.getDateTimeSummary({
                startDateTimeObj: dayjs(selectedRate.starts),
                endDateTimeObj: dayjs(selectedRate.ends),
                isMonthly: false,
            });
        }
    }
    // TODO This will need to accurately display info on modal for powerbooking - Part of DRAC-1638
    const priceLabel = (<Button variant="tertiary" display="block" width="100%" text-align="right" onClick={onShowRateDetailsClick} textDecoration="underline" color="text.secondary.light" fontWeight="normal" fontSize="sm" textAlign="right">
            {labelOversizeIncluded}
        </Button>);
    const inAndOutReservationSummary = () => selectedRate ? (<InOutReservationSummary starts={isPowerBooking
            ? powerBookingPeriods[0]?.starts
            : selectedRate.starts} ends={isPowerBooking
            ? powerBookingPeriods[0]?.ends
            : selectedRate.ends} hasInOutAmenity={hasInOutAmenity}/>) : null;
    return (<SpotDetailsReservationSummary isMobile={isMobile} isLoadingRates={isLoadingRates} displayPrice={price.value} priceLabel={priceLabel} currencyType={price.currencyCode} reservationDateTimeFormatted={reservationDateTimeFormatted} isReservationExtended={isReservationExtended} showSpotUnavailable={showSpotUnavailable} isSticky={isSticky} isShowing={isStickyVersionShowing} showEditTimesButton={appleMapsPartner && isMobile} onBookSpotClick={onBookSpotClick} onEditButtonClick={onToggleEditTimesModal} reservationExtensionEnabled={reservationExtensionEnabled} isNonRefundable={isNonRefundable} inOutReservationSummary={inAndOutReservationSummary} priceWithoutDiscount={priceWithoutDiscount} spotUnavailableDueToVehicleRestriction={spotUnavailableDueToVehicleRestriction} availability={availability} tieredAvailability={tieredAvailability}/>);
};
